import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Copy, Search, Star } from 'lucide-react';
import { AudienceLevel, LEVEL_LABELS, LEVEL_ORDER, PathwayTemplate } from '../../types/pathway';
import { moduleById } from '../../data/skillModules';
import { formatHours, totalHours } from '../../utils/pathwayMatching';
import { SelectDropdown } from '../SelectDropdown';
import { ResetFiltersButton } from '../ResetFiltersButton';
import { PathwayPagination } from './PathwayPagination';
const PAGE_SIZE = 5;
interface ProvenPathwaysProps {
  templates: PathwayTemplate[];
  onUseTemplate: (template: PathwayTemplate) => void;
}
function RankingStars({
  rating
}: {
  rating: number;
}) {
  return <span className="flex items-center gap-1.5" data-id="element-1741" data-mp="19uax3nl8z7v7">
      <span className="flex" aria-hidden="true" data-id="element-1742" data-mp="3963xlu78uvco">
        {[1, 2, 3, 4, 5].map(step => <Star key={step} size={13} className={step <= Math.round(rating) ? 'fill-[#30718D] text-[#30718D]' : 'text-gray-300'} data-id="element-1743" data-mp="346r44qlvaq14" />)}
      </span>
      <span className="text-xs text-gray-500" data-id="element-1744" data-mp="omsp4d2mos6u">{rating.toFixed(1)}</span>
      <span className="sr-only" data-id="element-1745" data-mp="an0510llxa7u">
        Ranked {rating.toFixed(1)} out of 5 by the teams who ran it
      </span>
    </span>;
}

/**
 * Pre-built sequences anyone can start from. Deliberately kept outside the
 * saved-pathways folder so removing that feature leaves these intact.
 */
export function ProvenPathways({
  templates,
  onUseTemplate
}: ProvenPathwaysProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [audienceFilter, setAudienceFilter] = useState('all');
  const [sort, setSort] = useState('utilized');
  const [page, setPage] = useState(1);
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = templates.filter(template => {
      if (audienceFilter !== 'all' && template.audience !== audienceFilter) return false;
      if (needle && !`${template.name} ${template.summary}`.toLowerCase().includes(needle)) {
        return false;
      }
      return true;
    });
    return filtered.slice().sort((a, b) => {
      if (sort === 'audience') {
        return LEVEL_ORDER.indexOf(a.audience) - LEVEL_ORDER.indexOf(b.audience) || a.name.localeCompare(b.name);
      }
      if (sort === 'longest') return totalHours(b.moduleIds) - totalHours(a.moduleIds);
      if (sort === 'shortest') return totalHours(a.moduleIds) - totalHours(b.moduleIds);
      if (sort === 'size') return b.moduleIds.length - a.moduleIds.length;
      if (sort === 'ranked') return b.rating - a.rating;
      return b.organizationsUsing - a.organizationsUsing;
    });
  }, [templates, query, audienceFilter, sort]);
  const isFiltered = query.trim().length > 0 || audienceFilter !== 'all' || sort !== 'utilized';
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageItems = visible.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const resetFilters = () => {
    setQuery('');
    setAudienceFilter('all');
    setSort('utilized');
    setPage(1);
  };
  return <section aria-labelledby="proven-pathways-heading" className="mt-10" data-id="element-1746" data-mp="3js8uw1o6el8f">
      <div className="mb-5" data-id="element-1747" data-mp="2n66yg8w6re08">
        <h2 id="proven-pathways-heading" className="text-xl font-bold text-gray-900" data-id="element-1748" data-mp="2r5xogadw1q3p">
          Proven pathways
        </h2>
        <p className="mt-1 max-w-xl text-sm text-gray-600" data-id="element-1749" data-mp="2w3hc20wh7f8b">
          Pre-built sequences your team already sells. Duplicate one and edit it
          for your prospect.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3" data-id="element-1750" data-mp="23flih527e3fr">
        <label className="relative" data-id="element-1751" data-mp="28ey2zj5lqyf6">
          <span className="sr-only" data-id="element-1752" data-mp="28dvdnfnxh41d">Search proven pathways</span>
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1753" data-mp="ew7kmtaecnef" />
          <input value={query} onChange={e => {
          setQuery(e.target.value);
          setPage(1);
        }} placeholder="Search proven pathways" className="w-64 rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1754" data-mp="9zh1ty77jdpb" />
        </label>
        <SelectDropdown ariaLabel="Filter proven pathways by audience" scrollMenu value={audienceFilter} onChange={value => {
        setAudienceFilter(value);
        setPage(1);
      }} options={[{
        value: 'all',
        label: 'All audiences'
      }, ...LEVEL_ORDER.map((level: AudienceLevel) => ({
        value: level,
        label: LEVEL_LABELS[level]
      }))]} data-id="element-1755" data-mp="16nn4ifkkjw0y" />
        <SelectDropdown ariaLabel="Sort proven pathways" scrollMenu value={sort} onChange={value => {
        setSort(value);
        setPage(1);
      }} options={[{
        value: 'utilized',
        label: 'Most utilized'
      }, {
        value: 'ranked',
        label: 'Top ranked'
      }, {
        value: 'audience',
        label: 'Audience level'
      }, {
        value: 'longest',
        label: 'Longest duration'
      }, {
        value: 'shortest',
        label: 'Shortest duration'
      }, {
        value: 'size',
        label: 'Most modules'
      }]} data-id="element-1756" data-mp="2uc40bpmdui7c" />
        <ResetFiltersButton isActive={isFiltered} onReset={resetFilters} data-id="element-1757" data-mp="26k5l4ujtc2ox" />
      </div>

      {visible.length === 0 ? <div className="rounded-xl border border-gray-100 bg-white px-6 py-10 text-center shadow-sm" data-id="element-1758" data-mp="3vyk6d4vmh7fo">
          <p className="text-sm text-gray-600" data-id="element-1759" data-mp="4tbwzivred5b">
            No proven pathways match those filters.
          </p>
          <button type="button" onClick={resetFilters} className="mt-2 text-sm font-medium text-[#30718D] transition-colors hover:text-[#265a70]" data-id="element-1760" data-mp="2f6s9j5tdz58">
            Clear filters
          </button>
        </div> : <div data-id="element-1761" data-mp="3kjqmg74jsn14">
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm" data-id="element-1762" data-mp="3kka1ng2tcxbg">
          <div className="overflow-x-auto" data-id="element-1763" data-mp="m1uz8u6xnyjj">
            <table className="w-full table-fixed sm:min-w-[960px]" data-id="element-1764" data-mp="m1lbld9d23h0">
              <caption className="sr-only" data-id="element-1765" data-mp="h2rwjgefe4eq">
                Proven pathways — expand a row to see its modules
              </caption>
              <colgroup data-id="element-1766" data-mp="cagvubbv2el4">
                <col data-id="element-1767" data-mp="c9yrssiyiw2n" />
                <col className="w-[190px]" data-id="element-1768" data-mp="7dqhbsfpfr1t" />
                <col className="w-[110px]" data-id="element-1769" data-mp="7cp35f3e6ofv" />
                <col className="w-[110px]" data-id="element-1770" data-mp="14scgo2mbf7pl" />
                <col className="w-[130px]" data-id="element-1771" data-mp="14te12xp816jz" />
                <col className="w-[140px]" data-id="element-1772" data-mp="101w4elu8ds4t" />
              </colgroup>
              <thead data-id="element-1773" data-mp="100l08kwviid6">
                <tr className="border-b border-gray-200 bg-gray-50" data-id="element-1774" data-mp="1002wl37vt1v8">
                  <th className="whitespace-nowrap px-3 py-3 text-left text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1775" data-mp="17evad9mxictr">
                    Pathway
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-left text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1776" data-mp="1hayjx0k41dqq">
                    Audience
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-center text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1777" data-mp="1qbwkt9z95553">
                    Modules
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-center text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1778" data-mp="1v8wnrc0t1rrz">
                    Utilized
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-left text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1779" data-mp="24zhk0st0ak7q">
                    Ranking
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-right text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1780" data-mp="2e0z8aygkhs7i">
                    <span className="sr-only" data-id="element-1781" data-mp="2zbe8tctl1s8o">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1782" data-mp="2vaaskp7mhu6x">
                {pageItems.map(template => {
                const expanded = expandedId === template.id;
                return <React.Fragment key={template.id} data-id="element-1783" data-mp="34b0jpflxuweb">
                      <tr className="border-b border-gray-100 transition-colors hover:bg-gray-50" data-id="element-1784" data-mp="349fp5pzmjdgg">
                        <td className="px-3 py-4 sm:px-6" data-id="element-1785" data-mp="2gjv9bfyrowbe">
                          <button type="button" onClick={() => setExpandedId(expanded ? null : template.id)} aria-expanded={expanded} className="flex items-start gap-2 text-left" data-id="element-1786" data-mp="2bnwfvdyl0o44">
                            <ChevronDown size={16} aria-hidden="true" className={`mt-0.5 flex-shrink-0 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} data-id="element-1787" data-mp="1swvd0eucc8fa" />
                            <span data-id="element-1788" data-mp="3vgxu838vu09r">
                              <span className="block text-sm font-semibold text-gray-900" data-id="element-1789" data-mp="3eq62rkda8wr">
                                {template.name}
                              </span>
                              <span className="mt-0.5 block text-xs text-gray-500" data-id="element-1790" data-mp="r2zk37bc9sdc">
                                {template.summary}
                              </span>
                            </span>
                          </button>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700 sm:px-6" data-id="element-1791" data-mp="n1nxlanrhn9d">
                          {LEVEL_LABELS[template.audience]}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-center text-sm text-gray-700 sm:px-6" data-id="element-1792" data-mp="37u7s71wja5p5">
                          {template.moduleIds.length}
                          <span className="ml-1 text-xs text-gray-500" data-id="element-1793" data-mp="ztdnrie5eycj">
                            · {formatHours(totalHours(template.moduleIds))}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-center text-sm text-gray-700 sm:px-6" data-id="element-1794" data-mp="qzqv4158mb7k">
                          {template.organizationsUsing}
                          <span className="ml-1 text-xs text-gray-500" data-id="element-1795" data-mp="h2crj24exgwo">
                            orgs
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 sm:px-6" data-id="element-1796" data-mp="34fts2e834gf">
                          <RankingStars rating={template.rating} data-id="element-1797" data-mp="82psgmz5on6s" />
                        </td>
                        <td className="px-3 py-4 sm:px-6" data-id="element-1798" data-mp="1xhnv8cwpeeb1">
                          <div className="flex items-center justify-end" data-id="element-1799" data-mp="22fp98jf9vnfn">
                            <button type="button" onClick={() => onUseTemplate(template)} className="flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-[#30718D] transition-colors hover:text-[#265a70]" data-id="element-1800" data-mp="22dmn4ah8qeg7">
                              <Copy size={14} data-id="element-1801" data-mp="2gkpl2d1la240" />
                              Duplicate
                            </button>
                          </div>
                        </td>
                      </tr>

                      <AnimatePresence initial={false} data-id="element-1802" data-mp="2qbtz9ajfxixs">
                        {expanded && <tr data-id="element-1803" data-mp="34b3iyk6uuv6i">
                            <td colSpan={6} className="border-b border-gray-100 bg-white p-0" data-id="element-1804" data-mp="34aa8p98otnjm">
                              <motion.div initial={{
                          opacity: 0,
                          height: 0
                        }} animate={{
                          opacity: 1,
                          height: 'auto'
                        }} exit={{
                          opacity: 0,
                          height: 0
                        }} transition={{
                          duration: 0.2,
                          ease: [0.23, 1, 0.32, 1]
                        }} className="overflow-hidden" data-id="element-1805" data-mp="19xkc2jqxq08j">
                                <ol className="my-2 ml-6 space-y-1.5 border-l-2 border-[#30718D]/20 pl-4 pr-3 sm:ml-10 sm:pr-6" data-id="element-1806" data-mp="3jv54p0sefu4i">
                                  {template.moduleIds.map((moduleId, index) => {
                              const module = moduleById(moduleId);
                              if (!module) return null;
                              return <li key={moduleId} className="flex items-baseline gap-3 text-sm" data-id="element-1807" data-mp="6hjze0spzcnh" data-mpi={index}>
                                        <span className="w-4 flex-shrink-0 text-xs font-bold text-[#30718D]" data-id="element-1808" data-mp="2012dq2lfpnf1">
                                          {index + 1}
                                        </span>
                                        <span className="font-medium text-gray-900" data-id="element-1809" data-mp="1r043h2kekjo0">
                                          {module.name}
                                        </span>
                                        <span className="text-xs text-gray-500" data-id="element-1810" data-mp="2jp1m56sj0iha">
                                          {formatHours(module.durationHours)} ·{' '}
                                          {module.category}
                                        </span>
                                      </li>;
                            })}
                                </ol>
                              </motion.div>
                            </td>
                          </tr>}
                      </AnimatePresence>
                    </React.Fragment>;
              })}
              </tbody>
            </table>
          </div>
        </div>
        <PathwayPagination label="Proven pathways pages" page={safePage} pageCount={pageCount} onChange={setPage} data-id="element-1811" data-mp="2m8mv4u5ut2iz" />
        </div>}
    </section>;
}